import React, { useMemo, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Card, CardBody } from "reactstrap";
import { withTranslation } from "react-i18next";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import ElectricMeterIcon from "@mui/icons-material/ElectricMeter";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RequestPageIcon from "@mui/icons-material/RequestPage";
import UserIcon from "@mui/icons-material/Person3";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import FeedIcon from "@mui/icons-material/Feed";
import EditIcon from "@mui/icons-material/Edit";
import PropTypes from "prop-types";

import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import {
  getOptionLabel,
  getOptionValue,
  tariff,
  utilityConsumerStatusEnum,
  utilityOptions,
} from "common/enums";
import SelectField from "Components/Common/SelectField";
import {
  allAdminRoles,
  CONSUMER,
  DCS_CONSUMER_APPLICATION_INCHARGE,
  otherUtilitiesApplicationAdmin3Roles,
  powerAdmin2Roles,
  powerAdmin3Roles,
  powerApplicationsRoles,
  SUPER_ADMIN,
} from "common/constant";
import UtilityConsumerViewModal from "./UtilityConsumerViewModal";
import Tooltip from "Components/Common/Tooltip";
import { ViewUtilityConsumerMeterInfo } from "./ViewUtilityConsumerMeterInfo";
import InputFieldText from "Components/Common/InputFieldText";
import ViewConsumerApplications from "./ViewConsumerApplications";
import UtilityConsumerEditModal from "./UtilityConsumerEditModal";
import ViewOtherUtilityMeterInfo from "./ViewOtherUtilityMeterInfo";
import MeterInfoForm from "pages/Admin/OtherApplicationRequest/MeterInfoForm";
import UtilityConsumerLoadTracking from "./UtilityConsumerLoadTracking";
import EditUtilityConsumerMeterInfo from "./EditUtilityConsumerMeterInfo";
import UpdateConsumerFinalReading from "./UpdateConsumerFinalReading";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import {
  AddEditMeterIcon,
  ViewDemandEstimationDocumentIcon,
} from "pages/Admin/PowerApplicationRequest/style";
import SendMailModal from "./SendMailModal";
import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import RefundCalculations from "pages/Admin/RefundCalculations";
import RefundBankDetailsFormModal from "pages/Admin/PowerApplicationRequest/RefundBankDetailsFormModal";
import Modal from "Components/Common/Modal";
import PaymentRequest from "Components/PaymentRequest";
import BankInformationFormModal from "pages/Consumer/BankInformationFormModal";

import { getImageUrl, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { UTILITY_CONSUMER } from "tagTypes";

import { usePostRefundCalculationPaymentMutation } from "apis/DemandEstimation";
import { usePutRemoveMeterMutation } from "apis/ApplicationRequestDetail";

import {
  ApplicationIcon,
  EditMeterIcon,
  SolarMeterIcon,
  MeterIcon,
} from "./style";
import ViewUserDetails from "pages/Admin/User/AuthorizedUsers/ViewUserDetails";

const RECORDS_PER_PAGE = 10;

const filterValues = {
  utility: "",
  tariff: "",
  status: "",
  consumerName: "",
  consumerNumber: "",
  address: "",
  meterNumber: "",
};

const UtilityConsumer = ({ t }) => {
  const [removeMeter, { isLoading: isMeterRemoving }] =
    usePutRemoveMeterMutation();
  const [getPaymentUrlForRefundCalculation] =
    usePostRefundCalculationPaymentMutation();

  const [
    consumerIdForRefundCalculationModal,
    setConsumerIdForRefundCalculationModal,
  ] = useState("");
  const [utilityConsumerModal, setUtilityConsumerModal] = useState(false);
  const [isEditBtnClicked, setIsEditBtnClicked] = useState(false);
  const [utilityConsumerMeterId, setUtilityConsumerMeterId] = useState("");
  const [isEditMeterBtnClicked, setIsEditMeterBtnClicked] = useState(false);
  const [solarMeterEditModalProps, setSolarMeterEditModalProps] =
    useState(null);
  const [applicationTypeId, setApplicationTypeId] = useState("");

  const [isViewMeterBtnClicked, setIsViewMeterBtnClicked] = useState(false);
  const [isViewSolarMeterBtnClicked, setIsViewSolarMeterBtnClicked] =
    useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [newAuthorizedUserId, setNewAuthorizedUserId] = useState("");
  const [utilityId, setUtilityId] = useState("");
  const [bankInformationModalProps, setBankInformationModalProps] =
    useState(null);
  const [
    consumerIdIdForRefundPaymentInformation,
    setConsumerIdForRefundPaymentInformation,
  ] = useState("");
  const [utilityConsumerIdForRemoveMeter, setUtilityConsumerIdForRemoveMeter] =
    useState(false);
  const [payNowModalProps, setPayNowModalProps] = useState(null);
  const [isSendMailBtnClicked, setIsSendMailBtnClicked] = useState(false);
  const [updateFinalReadingModalProps, setUpdateFinalReadingModalProps] =
    useState(null);
  const [tariffId, setTariffId] = useState("");

  const [
    utilityConsumerIdForApplications,
    setUtilityConsumerIdForApplications,
  ] = useState("");
  const [utilityConsumerIdForLoadDetails, setUtilityConsumerIdForLoadDetails] =
    useState("");
  const [utilityConsumerId, setUtilityConsumerId] = useState("");
  const [utilityConsumerIdForEditMeter, setUtilityConsumerIdForEditMeter] =
    useState("");
  const [filteredValues, setFilteredValues] = useState(filterValues);
  const [query, setQuery] = useState({});
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const lg = useMediaQuery("(min-width: 900px) and (max-width: 1199px)");
  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const { roles, user: { utilityRoles = [] } = {} } = useSelector(
    (state) => state.Login
  );

  const resetPageRef = useRef();

  const viewUtilityConsumerToggle = () => {
    setUtilityConsumerModal(!utilityConsumerModal);
    setUtilityId("");
  };

  const handleView = (consumerId, utilityId) => {
    setUtilityId(utilityId);
    setUtilityConsumerId(consumerId);
    setUtilityConsumerModal(true);
  };

  const handleEdit = (consumerId, utilityId, applicationType) => {
    setUtilityId(utilityId);
    setUtilityConsumerId(consumerId);
    setApplicationTypeId(applicationType);
    setIsEditBtnClicked(true);
  };

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const tariffOptions = useMemo(
    () => [{ value: "", label: "All" }, ...tariff],
    [tariff]
  );

  const utilityFilterOptions = useMemo(() => {
    const utilityRoleIds = utilityRoles.map(({ utilityId }) => utilityId);
    return [
      { value: "", label: "All" },
      ...(isAuthorizedUser(roles, [CONSUMER, SUPER_ADMIN])
        ? utilityOptions
        : utilityOptions.filter(({ value }) => utilityRoleIds.includes(value))),
    ];
  }, [utilityOptions, utilityRoles]);

  const columns = [
    {
      title: "Utility",
      renderCell: ({ utility }) => {
        return utility ? getOptionLabel("utilityOptions", utility) : "--";
      },
    },
    {
      title: "Customer Number",
      field: "consumerNumber",
    },
    {
      title: "Customer Name",
      field: "consumerName",
    },
    {
      title: "Utility Address",
      renderCell: ({ utilityAddress }) => {
        const address = formatUtilityAddress(utilityAddress);

        if (address) {
          return (
            <>
              <p className="m-0">{address}, Gandhinagar-382050</p>
            </>
          );
        }

        return "--";
      },
      columnProps: { style: { width: "350px", minWidth: "350px" } },
    },
    {
      title: "Status",
      renderCell: ({ status }) =>
        getOptionLabel("utilityConsumerStatusEnum", status) || "--",
    },
    {
      title: "Application Type",
      renderCell: ({ applicationType }) =>
        applicationType
          ? getOptionLabel("powerApplicationTypes", applicationType)
          : "--",
    },
    {
      title: "Tariff Name",
      renderCell: ({ tariff, isSolar, greenPower }) =>
        tariff ? (
          <>
            {getOptionLabel("tariff", tariff)} {isSolar && "(Net)"}
            {!!greenPower && "(Green Power)"}
          </>
        ) : (
          "--"
        ),
      columnProps: {
        style: {
          minWidth: "100px",
        },
      },
    },
    {
      title: "Date of Connection",
      renderCell: ({ dateOfConnection }) =>
        dateOfConnection
          ? convertUTCDateToLocalDate(dateOfConnection).format("DD-MM-YYYY")
          : "--",
    },
    {
      title: "Actions",
      renderCell: ({
        id,
        tariff,
        meterId,
        utility: currentUtilityIds,
        solarMeterId,
        isMeterAssigned,
        isMeterRemoved,
        status,
        bankDetailId,
        finalRefundedAmount,
        isRefundCalculatedByFinance,
        refundCalculationBreakupUrl,
        refundCalculationUrl,
        refundPaymentReceiptUrl,
        applicationType,
      }) => {
        return (
          <Box width="150px">
            <IconButton onClick={() => handleView(id, currentUtilityIds)}>
              <Tooltip title={t("View Utility Consumer Details")}>
                <RemoveRedEyeIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
            {roles.includes(SUPER_ADMIN) && (
              <IconButton
                onClick={() =>
                  handleEdit(id, currentUtilityIds, applicationType)
                }
              >
                <Tooltip title={t("Edit Utility Consumer's Details")}>
                  <EditIcon
                    fontSize="small"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}
            <IconButton
              onClick={() => {
                setUtilityConsumerIdForApplications(id);
                setUtilityId(currentUtilityIds);
              }}
            >
              <Tooltip title={t("View Consumer's Applications")}>
                <ApplicationIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>

            {!!meterId &&
              isAuthorizedUser(roles, [
                SUPER_ADMIN,
                DCS_CONSUMER_APPLICATION_INCHARGE,
                ...otherUtilitiesApplicationAdmin3Roles,
              ]) && (
                <IconButton
                  onClick={() => {
                    setUtilityConsumerMeterId(meterId);
                    setUtilityConsumerIdForEditMeter(id);
                    setIsEditMeterBtnClicked(true);
                    setUtilityId(currentUtilityIds);
                    setTariffId(tariff);
                  }}
                  disabled={!meterId}
                >
                  <Tooltip title={t("Edit Utility Consumer Meter Details")}>
                    <EditMeterIcon />
                  </Tooltip>
                </IconButton>
              )}

            {!!meterId && (
              <IconButton
                onClick={() => {
                  setUtilityConsumerId(id);
                  setIsViewMeterBtnClicked(true);
                  setUtilityId(currentUtilityIds);
                  setUtilityConsumerMeterId(meterId);
                }}
                disabled={!meterId}
              >
                <Tooltip title={t("View Utility Consumer Meter Details")}>
                  <MeterIcon />
                </Tooltip>
              </IconButton>
            )}

            {!!solarMeterId && roles.includes(SUPER_ADMIN) && (
              <IconButton
                onClick={() => {
                  setSolarMeterEditModalProps({
                    meterId: solarMeterId,
                    utilityConsumerId: id,
                  });
                }}
              >
                <Tooltip title={t("Edit Utility Consumer Solar Meter Details")}>
                  <ElectricMeterIcon color="primary" />
                </Tooltip>
              </IconButton>
            )}

            {!!solarMeterId && (
              <IconButton
                onClick={() => {
                  setUtilityConsumerId(id);
                  setIsViewSolarMeterBtnClicked(true);
                }}
              >
                <Tooltip title={t("View Utility Consumer Solar Meter Details")}>
                  <SolarMeterIcon />
                </Tooltip>
              </IconButton>
            )}

            {currentUtilityIds === getOptionValue("utilityOptions", "Power") &&
              isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin3Roles]) && (
                <>
                  <IconButton
                    onClick={() => {
                      setUtilityConsumerIdForLoadDetails(id);
                    }}
                  >
                    <Tooltip title={t("View Consumer's Updated Details")}>
                      <ArticleIcon
                        fontSize="small"
                        color="primary"
                        className="cursor-pointer"
                      />
                    </Tooltip>
                  </IconButton>
                </>
              )}

            {isMeterRemoved &&
              ((isAuthorizedUser(roles, powerAdmin3Roles) &&
                !refundCalculationBreakupUrl) ||
                (isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin2Roles]) &&
                  !!refundCalculationBreakupUrl)) &&
              ((!refundPaymentReceiptUrl && finalRefundedAmount < 0) ||
                (finalRefundedAmount > 0 && !bankDetailId) ||
                !finalRefundedAmount) && (
                <Tooltip title={t("Refund Calculations")}>
                  <IconButton
                    onClick={() => {
                      setConsumerIdForRefundCalculationModal(id);
                    }}
                    size="small"
                  >
                    <FeedIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}

            {isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin3Roles]) &&
              currentUtilityIds ===
                getOptionValue("utilityOptions", "Power") && (
                <>
                  {!!meterId &&
                    getOptionValue("utilityConsumerStatusEnum", "Activated") ===
                      status &&
                    !isMeterRemoved && (
                      <IconButton
                        onClick={() =>
                          setUpdateFinalReadingModalProps({
                            meterId,
                            consumerId: id,
                          })
                        }
                      >
                        <Tooltip
                          title={t(
                            "Add Final Meter Reading for Disconnection of Consumer"
                          )}
                        >
                          <AddEditMeterIcon />
                        </Tooltip>
                      </IconButton>
                    )}
                  {isMeterAssigned && !isMeterRemoved && (
                    <Tooltip title={t("Remove Meter")}>
                      <IconButton
                        onClick={() => setUtilityConsumerIdForRemoveMeter(id)}
                        size="small"
                      >
                        <CancelPresentationIcon color="primary" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}

            {!!bankDetailId &&
              !refundCalculationUrl &&
              isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin2Roles]) &&
              currentUtilityIds ===
                getOptionValue("utilityOptions", "Power") && (
                <IconButton
                  onClick={() => {
                    setConsumerIdForRefundPaymentInformation(id);
                  }}
                >
                  <Tooltip title={t("Complete Disconnection Process")}>
                    <CheckCircleIcon
                      fontSize="small"
                      className="cursor-pointer"
                      color="success"
                    />
                  </Tooltip>
                </IconButton>
              )}

            {finalRefundedAmount < 0 &&
              isRefundCalculatedByFinance &&
              roles.includes(CONSUMER) &&
              !refundPaymentReceiptUrl && (
                <Tooltip title="Pay Now">
                  <IconButton
                    onClick={() =>
                      setPayNowModalProps({
                        finalRefundedAmount,
                        consumerId: id,
                        isPowerUtility:
                          currentUtilityIds ===
                          getOptionValue("utilityOptions", "Power"),
                      })
                    }
                    size="small"
                  >
                    <CreditCardIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}

            {finalRefundedAmount > 0 &&
              roles.includes(CONSUMER) &&
              !refundCalculationUrl &&
              isRefundCalculatedByFinance && (
                <Tooltip title={t("Bank Information")}>
                  <IconButton
                    onClick={() => {
                      setBankInformationModalProps({
                        utilityConsumerId: id,
                      });
                    }}
                    size="small"
                  >
                    <AccountBalanceIcon color="primary" />
                  </IconButton>
                </Tooltip>
              )}

            {!!refundCalculationUrl && roles.includes(CONSUMER) && (
              <Tooltip title={t("Refund Calculations Document")}>
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(refundCalculationUrl),
                      documentTypeName: "Refund Calculations",
                      documentType: "pdf",
                      documentName: "Refund Calculations",
                      handleClose: () => setDocumentToPreview(null),
                    });
                  }}
                  size="small"
                >
                  <InsertDriveFileIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}

            {!!refundPaymentReceiptUrl && (
              <Tooltip title={t("Payment Receipt Document")}>
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(refundPaymentReceiptUrl),
                      documentTypeName: "Payment Receipt Document",
                      documentType: "pdf",
                      documentName: "Payment Receipt Document",
                      handleClose: () => setDocumentToPreview(null),
                    });
                  }}
                  size="small"
                >
                  <ViewDemandEstimationDocumentIcon />
                </IconButton>
              </Tooltip>
            )}

            {!!refundCalculationBreakupUrl && !roles.includes(CONSUMER) && (
              <Tooltip title={t("Refund Calculations Breakup Document")}>
                <IconButton
                  onClick={() => {
                    setDocumentToPreview({
                      url: getImageUrl(refundCalculationBreakupUrl),
                      documentTypeName: "Refund Calculations Breakup Document",
                      documentType: "pdf",
                      documentName: "Refund Calculations Breakup",
                      handleClose: () => setDocumentToPreview(null),
                    });
                  }}
                  size="small"
                >
                  <RequestPageIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {!roles.includes(CONSUMER) && (
              <Tooltip title={t("Authorized User Details")}>
                <IconButton
                  onClick={() => {
                    setNewAuthorizedUserId(id);
                  }}
                  size="small"
                >
                  <UserIcon color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/utility-consumer",
      tags: [UTILITY_CONSUMER],
      query,
    }),
    [query]
  );

  const handleChange = (e, key) => {
    setFilteredValues((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const searchBtnHandler = (e) => {
    e.preventDefault();
    setQuery(filteredValues);
    handleResetPage();
  };

  const clearBtnHandler = () => {
    setQuery({});
    setFilteredValues(filterValues);
    handleResetPage();
  };
  return (
    <>
      {utilityConsumerModal && (
        <UtilityConsumerViewModal
          show={utilityConsumerModal}
          utilityId={utilityId}
          handleClose={viewUtilityConsumerToggle}
          utilityConsumerId={utilityConsumerId}
        />
      )}

      {!!utilityConsumerIdForLoadDetails && (
        <UtilityConsumerLoadTracking
          utilityConsumerId={utilityConsumerIdForLoadDetails}
          handleClose={() => setUtilityConsumerIdForLoadDetails("")}
        />
      )}

      {isEditBtnClicked && utilityConsumerId && (
        <UtilityConsumerEditModal
          utilityConsumerId={utilityConsumerId}
          utilityIds={[utilityId]}
          applicationTypeId={applicationTypeId}
          handleClose={() => {
            setIsEditBtnClicked(false);
            setUtilityId("");
            setUtilityConsumerId("");
            setApplicationTypeId("");
          }}
        />
      )}

      {!!updateFinalReadingModalProps && (
        <UpdateConsumerFinalReading
          meterId={updateFinalReadingModalProps.meterId}
          consumerId={updateFinalReadingModalProps.consumerId}
          handleClose={() => setUpdateFinalReadingModalProps(null)}
        />
      )}

      <ConfirmationDialog
        isOpen={utilityConsumerIdForRemoveMeter}
        title="Warning"
        message="Are you sure you want to remove this meter?"
        onCancel={() => setUtilityConsumerIdForRemoveMeter("")}
        onConfirm={async () => {
          try {
            await removeMeter(utilityConsumerIdForRemoveMeter, {
              isForApplication: false,
              isMeterRemoved: true,
            });
            setUtilityConsumerIdForRemoveMeter("");
          } catch (error) {}
        }}
        isLoading={isMeterRemoving}
        variant="warning"
      />

      {!!consumerIdForRefundCalculationModal && (
        <RefundCalculations
          utilityConsumerId={consumerIdForRefundCalculationModal}
          handleClose={() => {
            setConsumerIdForRefundCalculationModal("");
          }}
        />
      )}

      {!!consumerIdIdForRefundPaymentInformation && (
        <RefundBankDetailsFormModal
          utilityConsumerId={consumerIdIdForRefundPaymentInformation}
          handleClose={() => {
            setConsumerIdForRefundPaymentInformation("");
          }}
          isUtilityConsumerModal
        />
      )}

      {!!utilityConsumerIdForApplications && (
        <ViewConsumerApplications
          utilityId={utilityId}
          consumerId={utilityConsumerIdForApplications}
          handleClose={() => {
            setUtilityConsumerIdForApplications("");
            setUtilityId("");
          }}
        />
      )}

      {!!payNowModalProps && (
        <Modal
          isOpen
          handleClose={() => {
            setPayNowModalProps(null);
          }}
          title="Refund Calculations Payment"
          maxWidth="sm"
        >
          <div className="mt-3">
            <Typography>
              You need to pay
              <b> RS. {Math.abs(payNowModalProps.finalRefundedAmount)}/- </b>
              to complete the application process.
            </Typography>
          </div>

          <div className="text-end mt-3">
            <PaymentRequest
              generateEncryption={async () =>
                await getPaymentUrlForRefundCalculation({
                  utilityConsumerId: payNowModalProps.consumerId,
                })
              }
              isPowerUtility={payNowModalProps.isPowerUtility}
            >
              <Button variant="contained">{t("Pay Now")}</Button>
            </PaymentRequest>
          </div>
        </Modal>
      )}

      {!!bankInformationModalProps && (
        <BankInformationFormModal
          bankInformationModalProps={bankInformationModalProps}
          handleClose={() => {
            setBankInformationModalProps(null);
          }}
        />
      )}

      {!!utilityConsumerId &&
        isViewMeterBtnClicked &&
        !!utilityConsumerMeterId && (
          <>
            {utilityId === getOptionValue("utilityOptions", "Power") ? (
              <ViewUtilityConsumerMeterInfo
                utilityConsumerId={utilityConsumerId}
                handleClose={() => {
                  setUtilityConsumerId("");
                  setIsViewMeterBtnClicked(false);
                }}
              />
            ) : (
              <ViewOtherUtilityMeterInfo
                handleClose={() => {
                  setIsViewMeterBtnClicked(false);
                  setUtilityConsumerMeterId("");
                }}
                meterId={utilityConsumerMeterId}
              />
            )}
          </>
        )}

      {!!utilityConsumerId && isViewSolarMeterBtnClicked && (
        <ViewUtilityConsumerMeterInfo
          utilityConsumerId={utilityConsumerId}
          handleClose={() => {
            setUtilityConsumerId("");
            setIsViewSolarMeterBtnClicked(false);
          }}
          isSolarModalOpen
        />
      )}

      {!!documentToPreview && (
        <DocumentPreviewModal isOpen {...documentToPreview} />
      )}

      {!!newAuthorizedUserId && (
        <ViewUserDetails
          userId={newAuthorizedUserId}
          handleClose={() => setNewAuthorizedUserId("")}
          isFetchingNewAuthorized
        />
      )}

      {isEditMeterBtnClicked && !!utilityConsumerMeterId && (
        <>
          {utilityId === getOptionValue("utilityOptions", "Power") ? (
            <EditUtilityConsumerMeterInfo
              meterId={utilityConsumerMeterId}
              handleClose={() => {
                setUtilityConsumerMeterId("");
                setUtilityConsumerIdForEditMeter("");
                setIsEditMeterBtnClicked(false);
              }}
              consumerId={utilityConsumerIdForEditMeter}
            />
          ) : (
            <MeterInfoForm
              handleClose={() => {
                setUtilityConsumerMeterId("");
                setUtilityConsumerIdForEditMeter("");
                setIsEditMeterBtnClicked(false);
                setUtilityId("");
                setTariffId("");
              }}
              applicationData={{
                meterId: utilityConsumerMeterId,
                consumerId: utilityConsumerIdForEditMeter,
                utilityId,
                tariffId,
              }}
              isFinalReadingRequired
            />
          )}
        </>
      )}

      {!!solarMeterEditModalProps && (
        <EditUtilityConsumerMeterInfo
          meterId={solarMeterEditModalProps.meterId}
          handleClose={() => {
            setSolarMeterEditModalProps(null);
          }}
          consumerId={solarMeterEditModalProps.utilityConsumerId}
          isSolarMeter
        />
      )}

      {isSendMailBtnClicked && (
        <SendMailModal
          handleClose={() => setIsSendMailBtnClicked(false)}
          utility={filteredValues?.utility ?? ""}
        />
      )}

      <div className="h-100">
        <BreadCrumb title={t("Utility Consumers")} />
        <form onSubmit={searchBtnHandler}>
          <Grid container columnSpacing={1} className="mb-1">
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <SelectField
                label={t("Utility")}
                value={utilityFilterOptions.find(
                  ({ value }) => value === filteredValues.utility
                )}
                onChange={(_, { value }) => {
                  setFilteredValues((prev) => ({ ...prev, utility: value }));
                }}
                options={utilityFilterOptions}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <InputFieldText
                label={t("Consumer Number")}
                placeholder="Enter Consumer Number"
                value={filteredValues.consumerNumber}
                type="number"
                onChange={(e) => handleChange(e, "consumerNumber")}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <InputFieldText
                label={t("Consumer Name")}
                placeholder="Enter Consumer Name"
                value={filteredValues.consumerName}
                onChange={(e) => handleChange(e, "consumerName")}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <InputFieldText
                label={t("Address")}
                placeholder="Enter Address"
                value={filteredValues.address}
                onChange={(e) => handleChange(e, "address")}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <InputFieldText
                label={t("Meter Number")}
                placeholder="Enter Meter Number"
                value={filteredValues.meterNumber}
                onChange={(e) => handleChange(e, "meterNumber")}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <SelectField
                label={t("Tariff Category")}
                value={tariffOptions.find(
                  ({ value }) => value === filteredValues.tariff
                )}
                onChange={(_, { value }) => {
                  setFilteredValues((prev) => ({ ...prev, tariff: value }));
                }}
                options={tariffOptions}
              />
            </Grid>
            <Grid item xs={6} md={4} lg={3} xl={2}>
              <SelectField
                label={t("Status")}
                value={utilityConsumerStatusEnum.find(
                  ({ value }) => value === filteredValues.status
                )}
                onChange={(_, { value }) => {
                  setFilteredValues((prev) => ({ ...prev, status: value }));
                }}
                options={utilityConsumerStatusEnum}
              />
            </Grid>

            <Grid item>
              <Button variant="outlined" className="mb-2 me-2" type="submit">
                {t("Search")}
              </Button>
              <Button
                variant="outlined"
                className="mb-2 me-2"
                onClick={clearBtnHandler}
              >
                {t("Clear")}
              </Button>
              {isAuthorizedUser(roles, allAdminRoles) && (
                <Button
                  variant="contained"
                  className="mb-2 me-2"
                  onClick={() => setIsSendMailBtnClicked(true)}
                  disabled={!filteredValues.utility }
                >
                  {t("Send Mail")}
                </Button>
              )}
            </Grid>
          </Grid>
        </form>
        <Card className="p-0">
          <CardBody className="p-0">
            <BaseTable
              columns={columns}
              pageSize={RECORDS_PER_PAGE}
              paginationType="serverSide"
              serverSidePaginationConfig={serverSidePaginationConfig}
              registerResetPage={(cb) => {
                resetPageRef.current = cb;
                setIsResetPageRegistered((prev) => !prev);
              }}
              maxHeight={
                sm
                  ? "calc(100vh - 450px)"
                  : md
                  ? "calc(100vh - 420px)"
                  : lg
                  ? "calc(100vh - 360px)"
                  : "calc(100vh - 320px)"
              }
            />
          </CardBody>
        </Card>
      </div>
    </>
  );
};

UtilityConsumer.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(UtilityConsumer);
